// RightMenuOverlayText.js
// React toggle. Ensures the tool is single-use and fully destroyed on save or when turned off.

import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, HStack } from "@chakra-ui/react";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";
import { LuTypeOutline } from "react-icons/lu";
import { enableRectangleDrawingWithUI } from "../../../utilies/geojson/OverlayText";
import { addOverlayTextFeatureToDataSource } from "../../../utilies/geojson/LoadGeojsonOverlayText";
import { loadGeojsonTypeAsync } from "../../../utilies/loader/LoadGeojson";
import Type from "../../../utilies/Types";
import { useMapObjects } from "../../../context/MapObjectsContext";
function OverlayText() {
  // state
  const overlayTextHandlerRef = useAppStore((s) => s.overlayTextHandlerRef);
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);
  const overlayTextMode = useAppStore((s) => s.overlayTextMode);
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const defaultOsmStyle = useAppStore((s) => s.defaultOsmStyle);
  const userType = useAppStore((s) => s.userType);
  const setLineIntersectMeasureMode = useAppStore((s) => s.setLineIntersectMeasureMode);
  const setMeasureDistanceMode = useAppStore((s) => s.setMeasureDistanceMode);

  // setters
  const setOverlayTextMode = useAppStore.getState().setOverlayTextMode;

  const [show, setShow] = useState(false);
  useEffect(() => {
    if (userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, []);

  useEffect(() => {
    const viewer = viewerRef?.current;
    if (!viewer) return;

    // disable path: destroy immediately
    if (!overlayTextMode) {
      if (overlayTextHandlerRef.current?.destroy) {
        try {
          overlayTextHandlerRef.current.destroy();
        } catch {}
      }
      overlayTextHandlerRef.current = null;
      return;
    } else {
      setLineIntersectMeasureMode(false);
      setMeasureDistanceMode(false);
    }

    // enable path: create once
    if (!overlayTextHandlerRef.current) {
      const handler = enableRectangleDrawingWithUI(viewer, originalCameraControls, (feature) => {
        setOverlayTextMode(false);

        let temp = [...mapObjects];
        let isExist = false;
        for (const key in temp) {
          const isOverlayTextGeojson = temp[key].file_name
            .toLowerCase()
            .replace(/[\s\-_]/g, "")
            .includes("overlaytext");
          if (isOverlayTextGeojson) {
            temp[key].original_geojson.features.push(feature); // keep model
            const ds = temp[key].dataSource; // existing DS from loader
            addOverlayTextFeatureToDataSource({ viewer, dataSource: ds, feature }); // live add
            isExist = true;
            setMapObjects(temp);
            break;
          }
        }
        if (!isExist) {
          let geojsonData = {
            type: "FeatureCollection",
            crs: {
              type: "name",
              properties: {
                name: "urn:ogc:def:crs:OGC:1.3:CRS84",
              },
            },
            features: [feature],
          };
          loadGeojsonTypeAsync({
            originalCameraControls,
            geojsonData: geojsonData,
            fileName: "Overlay_text",
            viewerRef,
            setMapObjects,
            defaultGeojsonStyle,
            basicGeojsonStyle,
            defaultOsmStyle,
            isEdited: true,
          });
        }
      });
      overlayTextHandlerRef.current = handler;
    }

    // cleanup on unmount or dependency change
    return () => {
      if (overlayTextHandlerRef.current?.destroy) {
        try {
          overlayTextHandlerRef.current.destroy();
        } catch {}
      }
      overlayTextHandlerRef.current = null;
    };
  }, [overlayTextMode, viewerRef, originalCameraControls, setOverlayTextMode]);

  const toggle = () => setOverlayTextMode((v) => !v);

  if (!show) return null;
  return (
    <HStack>
      <Button disabled={editMode} variant="solid" p={0} opacity={1} bg={overlayTextMode ? "green" : "white"} color={overlayTextMode ? "white" : "MenuText"} onClick={toggle}>
        <Tooltip content="Overlay Text">
          <LuTypeOutline />
        </Tooltip>
      </Button>
    </HStack>
  );
}

export default OverlayText;
